import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { articlesAPI } from '@/lib/api';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  BookOpen,
  Search,
  Calendar,
  Eye,
  ArrowRight,
  Loader2,
  FileText,
  Globe,
  Award,
  Lightbulb
} from 'lucide-react';

const BlogPage = () => {
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [articlesRes, categoriesRes] = await Promise.all([
        articlesAPI.getAll({ limit: 50 }),
        articlesAPI.getCategories()
      ]);
      setArticles(articlesRes.data || []);
      setCategories(categoriesRes.data || []);
    } catch (error) {
      console.error('Error fetching articles:', error);
      // Try to seed sample articles if none exist
      try {
        await articlesAPI.seedSamples();
        const articlesRes = await articlesAPI.getAll({ limit: 50 });
        setArticles(articlesRes.data || []);
      } catch (seedError) {
        console.error('Error seeding articles:', seedError);
      }
    } finally {
      setLoading(false);
    }
  };

  const getCategoryIcon = (categoryId) => {
    const icons = {
      study_tips: <Lightbulb className="w-5 h-5" />,
      visa_guides: <FileText className="w-5 h-5" />,
      country_guides: <Globe className="w-5 h-5" />,
      success_stories: <Award className="w-5 h-5" />
    };
    return icons[categoryId] || <BookOpen className="w-5 h-5" />;
  };

  const getCategoryColor = (categoryId) => {
    const colors = {
      study_tips: 'bg-blue-100 text-blue-600 border-blue-500/30',
      visa_guides: 'bg-green-100 text-green-600 border-green-500/30',
      country_guides: 'bg-purple-100 text-purple-600 border-purple-500/30',
      success_stories: 'bg-teal-100 text-teal-600 border-yellow-500/30'
    };
    return colors[categoryId] || 'bg-gray-200 text-gray-500 border-slate-500/30';
  };

  const filteredArticles = articles.filter(article => {
    const matchesCategory = !selectedCategory || article.category === selectedCategory;
    const matchesSearch = !searchTerm || 
      article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      article.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const featuredArticle = articles[0];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f8f9fa] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-teal-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8f9fa] dark:bg-slate-900 transition-colors">
      <Navbar />
      
      <main className="pt-20 pb-12">
        {/* Hero */}
        <div className="bg-gradient-to-r from-teal-100/80 via-[#e9ecef] to-[#f1f3f5] py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h1 className="text-4xl font-semibold text-gray-800 mb-4">Blog &amp; Resources</h1>
            <p className="text-gray-600 text-lg max-w-2xl">
              Expert advice, guides, and success stories to help you navigate your study abroad journey.
            </p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Search and Categories - Bordered Section */}
          <div className="bg-white dark:bg-slate-800 border-2 border-gray-200 dark:border-slate-700 rounded-2xl p-6 mb-8 transition-colors">
            <div className="flex items-center gap-3 mb-5 pb-4 border-b border-gray-200 dark:border-slate-600">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center">
                <Search className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-800 dark:text-white">Browse Articles</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">Search and filter by category</p>
              </div>
            </div>
            
            {/* Search */}
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                type="text"
                placeholder="Search articles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-gray-50 dark:bg-slate-700 border-gray-200 dark:border-slate-600 text-gray-800 dark:text-white"
              />
            </div>

            {/* Categories */}
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => setSelectedCategory(null)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors border-2 ${
                  !selectedCategory 
                    ? 'bg-teal-600 text-white border-teal-600' 
                    : 'bg-gray-50 dark:bg-slate-700 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-slate-600 hover:border-teal-400'
                }`}
              >
                All Articles
              </button>
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors flex items-center gap-2 border-2 ${
                    selectedCategory === cat.id 
                      ? 'bg-teal-600 text-white border-teal-600' 
                      : 'bg-gray-50 dark:bg-slate-700 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-slate-600 hover:border-teal-400'
                  }`}
                >
                  {getCategoryIcon(cat.id)}
                  {cat.name}
                  <span className="text-xs opacity-70">({cat.count})</span>
                </button>
              ))}
            </div>
          </div>

          {/* Featured Article - Bordered */}
          {featuredArticle && !selectedCategory && !searchTerm && (
            <div className="mb-8">
              <Link to={`/blog/${featuredArticle.slug}`}>
                <div className="bg-white dark:bg-slate-800 border-2 border-gray-200 dark:border-slate-700 rounded-2xl overflow-hidden hover:border-teal-400 transition-all">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="aspect-video md:aspect-auto">
                      {featuredArticle.featured_image ? (
                        <img 
                          src={featuredArticle.featured_image} 
                          alt={featuredArticle.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-teal-100 to-[#e9ecef] dark:from-teal-900/30 dark:to-slate-700 flex items-center justify-center">
                          <BookOpen className="w-16 h-16 text-teal-600/50 dark:text-teal-400/50" />
                        </div>
                      )}
                    </div>
                    <div className="p-6 flex flex-col justify-center">
                      <Badge className={`w-fit mb-3 ${getCategoryColor(featuredArticle.category)}`}>
                        {categories.find(c => c.id === featuredArticle.category)?.name || featuredArticle.category}
                      </Badge>
                      <h2 className="text-2xl font-semibold text-gray-800 mb-3">{featuredArticle.title}</h2>
                      <p className="text-gray-500 mb-4">{featuredArticle.excerpt}</p>
                      <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {new Date(featuredArticle.created_at).toLocaleDateString()}
                        </span>
                        <span className="flex items-center gap-1">
                          <Eye className="w-4 h-4" />
                          {featuredArticle.views} views
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            </div>
          )}

          {/* Articles Grid */}
          {filteredArticles.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredArticles.slice(featuredArticle && !selectedCategory && !searchTerm ? 1 : 0).map((article) => (
                <Link key={article.id} to={`/blog/${article.slug}`}>
                  <article className="bg-white border border-gray-200 rounded-xl rounded-xl overflow-hidden hover:border-teal-300 transition-colors h-full flex flex-col">
                    <div className="aspect-video">
                      {article.featured_image ? (
                        <img 
                          src={article.featured_image} 
                          alt={article.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-teal-100 to-[#e9ecef] flex items-center justify-center">
                          <BookOpen className="w-10 h-10 text-teal-600/50" />
                        </div>
                      )}
                    </div>
                    <div className="p-5 flex flex-col flex-1">
                      <Badge className={`w-fit mb-3 ${getCategoryColor(article.category)}`}>
                        {categories.find(c => c.id === article.category)?.name || article.category}
                      </Badge>
                      <h3 className="text-lg font-semibold text-gray-800 mb-2 line-clamp-2">{article.title}</h3>
                      <p className="text-gray-500 text-sm mb-4 line-clamp-2 flex-1">{article.excerpt}</p>
                      <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {new Date(article.created_at).toLocaleDateString()}
                        </span>
                        <span className="flex items-center gap-1">
                          <Eye className="w-3 h-3" />
                          {article.views}
                        </span>
                      </div>
                    </div>
                  </article>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <BookOpen className="w-12 h-12 text-gray-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-800 mb-2">No Articles Found</h3>
              <p className="text-gray-500 dark:text-gray-400">
                {searchTerm ? 'Try a different search term' : 'Check back soon for new content'}
              </p>
            </div>
          )}

          {/* CTA Section */}
          <div className="mt-12 bg-white border border-gray-200 rounded-xl rounded-xl p-8 text-center">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">Ready to Start Your Journey?</h2>
            <p className="text-gray-500 mb-6 max-w-xl mx-auto">
              Our expert counselors are here to guide you every step of the way. Get personalized advice for your study abroad goals.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/check-eligibility">
                <Button className="bg-teal-600 text-white hover:bg-teal-700 font-semibold">
                  Check Your Eligibility
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/help">
                <Button variant="outline" className="border-gray-300 text-gray-600 hover:bg-gray-100">
                  Contact Us
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default BlogPage;
